import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { motion } from 'framer-motion';
import apiManager from '../../../lib/api';
import trackingManager from '../../../lib/tracking';
import { EventType } from '../../../lib/tracking/eventTracker';
import { Order as ApiOrder } from '../../../lib/api/models';
import { T } from '@tolgee/react';

interface Order {
  id: string;
  date: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'payment_failed' | 'refunded';
  total: number;
  items: number;
  trackingNumber?: string;
  recipientName?: string;
  notifyRecipient?: boolean;
}

const OrdersPage = () => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Cargar pedidos del usuario usando la API
  useEffect(() => {
    if (auth.user) {
      const fetchOrders = async () => {
        setIsLoading(true);
        try {
          // Registrar evento de vista de página
          trackingManager.events.track(
            EventType.PAGE_VIEW, 
            {
              path: window.location.pathname,
              pageTitle: document.title,
              customData: { section: 'orders' }
            }
          );
          
          // Rastrear evento personalizado
          trackingManager.events.track(
            EventType.CUSTOM,
            {
              eventName: 'view_orders_list',
              userId: auth.user?.id
            }
          );

          // Usar la API para obtener los pedidos
          const response = await apiManager.orders.getOrders();
          
          if (response.success && response.data) {
            // Transformar los datos de la API al formato que espera el componente
            const formattedOrders: Order[] = response.data.map((order: ApiOrder) => ({
              id: order.id,
              date: order.createdAt,
              status: order.status,
              total: order.totalAmount,
              items: order.products.length,
              trackingNumber: order.trackingNumber,
              recipientName: (order as any).dropShipping?.recipient?.name,
              notifyRecipient: (order as any).dropShipping?.notifyRecipient
            }));
            
            setOrders(formattedOrders);
          } else {
            console.error('Error al cargar pedidos:', response.error);
            setOrders([]);
          }
        } catch (error) {
          console.error('Error al cargar pedidos:', error);
          setOrders([]);
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchOrders();
    }
  }, [auth.user]);
  
  // Traducir el estado del pedido
  const getStatusKey = (status: Order['status']) => {
    const statusMap = {
      pending: 'auth.orders.pending',
      processing: 'auth.orders.processing',
      shipped: 'auth.orders.shipped',
      delivered: 'auth.orders.delivered',
      cancelled: 'auth.orders.cancelled',
      payment_failed: 'auth.orders.payment_failed',
      refunded: 'auth.orders.refunded'
    };
    return statusMap[status] || status;
  };

  // Obtener el color del estado
  const getStatusColor = (status: Order['status']) => {
    const colorMap = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      payment_failed: 'bg-red-100 text-red-800',
      refunded: 'bg-purple-100 text-purple-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };
  
  // Formatear fecha
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };
  
  // Manejar clic en un pedido
  const handleOrderClick = (orderId: string) => {
    // Registrar evento de clic en un pedido
    trackingManager.events.track(
      EventType.CUSTOM,
      {
        eventName: 'order_detail_click',
        userId: auth.user?.id,
        orderId: orderId
      }
    );
    
    navigate(`/orders/${orderId}`);
  };
  
  if (!auth.isAuthenticated) {
    navigate('/login');
    return null;
  }
  
  return (
    <div className="min-h-screen" style={{backgroundColor: '#f2ede4'}}>
      {/* Hero Section */}
      <div className="py-24 bg-white">
        <div className="content-container">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 tracking-tight" style={{color: '#0d0d0d'}}>
              <T keyName="auth.orders.title">Mis Pedidos</T>
            </h1>
            <p className="text-xl md:text-2xl font-light text-gray-600 mb-8 leading-relaxed">
              <T keyName="auth.orders.subtitle">Consulta el estado y detalles de tus pedidos recientes</T>
            </p>
            <Link
              to="/account"
              className="inline-flex items-center text-lg font-medium hover:underline transition-colors"
              style={{color: '#0d0d0d'}}
              onClick={() => trackingManager.events.track(
                EventType.CUSTOM,
                {
                  eventName: 'orders_back_to_account',
                  userId: auth.user?.id
                }
              )}
            >
              ← <T keyName="auth.orders.back_to_account">Volver a mi cuenta</T>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-16">
        <div className="content-container">
      
          {isLoading ? (
            <div className="bg-white shadow-md rounded-2xl p-8">
              <div className="animate-pulse flex flex-col space-y-6">
                <div className="h-6 rounded w-1/4" style={{backgroundColor: '#f6cf02', opacity: 0.3}}></div>
                <div className="space-y-3">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded"></div>
                </div>
                <div className="h-20 bg-gray-200 rounded-2xl"></div>
                <div className="h-20 bg-gray-200 rounded-2xl"></div>
              </div>
            </div>
      ) : orders.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="bg-white shadow-md rounded-2xl overflow-hidden">
                <div className="bg-white shadow-md overflow-hidden rounded-2xl">
              <ul className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <li key={order.id}>
                    <div className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col sm:flex-row sm:items-center">
                            <p className="text-sm font-bold truncate mr-4" style={{color: '#0d0d0d'}}>
                              <T keyName="auth.orders.order_number" params={{ id: order.id }}>{`Pedido #${order.id}`}</T>
                            </p>
                            <p className="mt-1 sm:mt-0 text-sm text-gray-500">
                              <span className="hidden sm:inline">•</span> {formatDate(order.date)}
                            </p>
                          </div>
                          <div className="ml-2 flex-shrink-0">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                              <T keyName={getStatusKey(order.status)}>{getStatusKey(order.status)}</T>
                            </span>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path>
                              </svg>
                              <T keyName="auth.orders.products_count" params={{ count: order.items.toString() }}>{`${order.items} ${order.items === 1 ? 'producto' : 'productos'}`}</T>
                            </p>
                            <p className="mt-2 sm:mt-0 sm:ml-6 flex items-center text-sm text-gray-500">
                              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                              <T keyName="auth.orders.total">Total</T>: €{order.total.toFixed(2)}
                            </p>
                            {order.trackingNumber && (
                              <p className="mt-2 sm:mt-0 sm:ml-6 flex items-center text-sm text-gray-500">
                                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                                </svg>
                                <T keyName="auth.orders.tracking">Tracking</T>: {order.trackingNumber}
                              </p>
                            )}
                            {order.recipientName && (
                              <p className="mt-2 sm:mt-0 sm:ml-6 flex items-center text-sm text-amber-700">
                                Destinatario: {order.recipientName}
                                {order.notifyRecipient ? ' · email logístico' : ''}
                              </p>
                            )}
                          </div>
                          <div className="ml-5 flex-shrink-0">
                            <motion.button 
                              onClick={() => handleOrderClick(order.id)}
                              className="px-4 py-2 rounded-full font-medium transition-all border-2 shadow-sm hover:shadow-md"
                              style={{
                                backgroundColor: '#f6cf02',
                                color: '#0d0d0d',
                                borderColor: '#0d0d0d'
                              }}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#0d0d0d';
                                e.currentTarget.style.color = '#f6cf02';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#f6cf02';
                                e.currentTarget.style.color = '#0d0d0d';
                              }}
                            >
                              <T keyName="auth.orders.view_details">Ver detalles</T>
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white shadow-md rounded-2xl overflow-hidden"
            >
          <div className="p-4 sm:p-6">
            <div className="text-center py-16">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">
                <T keyName="auth.orders.no_orders">No hay pedidos</T>
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                <T keyName="auth.orders.no_orders_message">Aún no has realizado ningún pedido.</T>
              </p>
              <div className="mt-6">
                <motion.div>
                  <Link
                    to="/products"
                    onClick={() => trackingManager.events.track(
                      EventType.CUSTOM,
                      {
                        eventName: 'empty_orders_click_products',
                        userId: auth.user?.id
                      }
                    )}
                    className="inline-flex items-center px-6 py-3 border-2 font-medium rounded-full shadow-md hover:shadow-lg transition-all"
                    style={{
                      backgroundColor: '#f6cf02',
                      color: '#0d0d0d',
                      borderColor: '#0d0d0d'
                    }}
                  >
                    <T keyName="auth.orders.explore_products">Explorar productos</T>
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
        </div>
      </div>
    </div>
  );
};

export default OrdersPage; 
